/**
 * Server-side PDF entry: open /opening-intelligence-report-print?token=...
 * No login cookie required; token is signed by the chess API.
 */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import OpeningPerformanceReport from '@/Components/OpeningPerformanceReport';
import CCColors from '@/Utils/CCColors';
import CCText from '@/Atoms/CCText';
import Environment from '../../../environment';

function readToken(): string {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return '';
  }
  return new URLSearchParams(window.location.search || '').get('token') || '';
}

const OpeningIntelligenceReportPrintWeb: React.FC = () => {
  const [payload, setPayload] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = readToken();
    if (!token) {
      setError('Missing print token.');
      return;
    }
    const base = (Environment as any).CHESS_API_URL || '';
    const apiKey = (Environment as any).API_KEY;
    const url = `${base.replace(/\/$/, '')}/assessment/v1/learning/get_opening_intelligence_report_print/?token=${encodeURIComponent(token)}`;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (apiKey) {
      headers['api-key'] = apiKey;
    }
    fetch(url, { headers })
      .then(async res => {
        const j = await res.json().catch(() => ({}));
        if (!res.ok || !j?.success) {
          setError(j?.error || `HTTP ${res.status}`);
          return;
        }
        setPayload(j);
      })
      .catch(e => setError(e?.message || String(e)));
  }, []);

  const toCountRows = (rows: Array<{ title?: string; count_label?: string; count?: number }> = []) =>
    rows.map(row => ({
      title: row.title || '',
      count: row.count_label || `${row.count || 0}`,
    }));

  if (error) {
    return (
      <View style={[styles.centered, styles.page]}>
        <CCText style={styles.errTitle}>Could not load report</CCText>
        <CCText style={styles.errBody}>{error}</CCText>
      </View>
    );
  }

  if (!payload) {
    return (
      <View style={[styles.centered, styles.page]}>
        <ActivityIndicator size="large" color={CCColors.Primary.Default} />
        <CCText style={styles.hint}>Loading opening report…</CCText>
      </View>
    );
  }

  const oirResponse = payload;
  const frontendView = oirResponse?.data?.frontend_view || {};

  return (
    <View style={styles.page}>
      <OpeningPerformanceReport
        forPdfExport
        openingScore={frontendView.opening_score}
        openingAccuracy={frontendView.opening_accuracy}
        bestGameScore={frontendView.best_game_score}
        lowestGameScore={frontendView.lowest_game_score}
        reportDate={frontendView.report_date}
        gamesAnalyzed={frontendView.games_analyzed}
        gameScores={frontendView.game_scores}
        gameScoreCards={oirResponse?.data?.game_by_game_performance || []}
        selectedOpening={frontendView.selected_opening}
        performanceCardsData={(frontendView.performance_cards || []).map((card: any) => ({
          title: card.title,
          content: card.content,
          tone: card.tone,
        }))}
        reportIntro={frontendView.report_intro}
        scoreBand={frontendView.score_band}
        scoreBandLegend={frontendView.opening_score_band_legend}
        sectionNotes={frontendView.section_notes}
        improvementsData={frontendView.improvements}
        recurringErrorsData={toCountRows(frontendView.recurring_errors)}
        opponentErrorsData={toCountRows(
          frontendView.recurring_opponent_errors || frontendView.opponent_errors,
        )}
        criticalPositionsData={toCountRows(frontendView.critical_positions)}
        modelGamesData={(frontendView.model_games || []).map((g: any) => ({
          title: g.title,
          reason: g.reason,
          model_game_id: g.model_game_id,
          pgn: g.pgn,
          event: g.event,
        }))}
        mostBrokenPrincipleQuote={frontendView.most_broken_principle?.quote}
        principleWiseScores={frontendView.principle_wise_scores}
        colorBreakdown={frontendView.color_breakdown}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: CCColors.Primary.Light,
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  hint: {
    marginTop: 12,
    color: CCColors.TextColor.Primary,
  },
  errTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
    color: CCColors.TextColor.Primary,
  },
  errBody: {
    color: CCColors.Red,
    textAlign: 'center',
  },
});

export default OpeningIntelligenceReportPrintWeb;
